import React, {
  Fragment,
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";

// d.ts
import {
  DrawingType,
  CoordinateSystemType,
  BoolSettings,
  ViewOptionsType,
  IMapView,
} from "@/@types/common/index";
import {
  IMapProps,
  IMapState,
  CardPosition,
  CardOverlayType,
  ICardOverlay,
  ICardOverlayData,
} from "@/@types/views/GIS/map";

// OpenLayers
import Geometry from "ol/geom/Geometry";
import proj4, { Point } from "proj4";
import { register as OlRegister } from "ol/proj/proj4";
import { get as OlGetProjection } from "ol/proj";
import Projection from "ol/proj/Projection";
import OlLayerGroup from "ol/layer/Group";
import OlSourceVector from "ol/source/Vector";
import Map from "@/components/Map/Map";
import OlMapBrowserEvent from "ol/MapBrowserEvent";
import OlBaseLayer from "ol/layer/Base";
import OlFeature from "ol/Feature";
import OlCollection from "ol/Collection";
import OlPoint from "ol/geom/Point";
import OlLineString from "ol/geom/LineString";
import OlMultiLineString from "ol/geom/MultiLineString";


// Custom
import {
  Controls,
  AttributionControl,
  CoordZoomStatusControl,
  DefaultControls,
  FullScreenControl,
  OverviewMapControl,
  RotateControl,
  ScaleLineControl,
  ScaleRatioControl,
  StatusControl,
  ZoomControl,
  ZoomToExtentControl,
  GeoLocateControl,
  RadiusRulerControl,
  ZoomSliderControl,
  CenterMarkerControl,
  LayerTree,
  ViewHistoryControl,
} from "@/components/Map/Controls";

import {
  Layers,
  VectorLayer,
} from "@/components/Map/Layers";
import {
  Overlays,
  PopupOverlay,
} from "@/components/Map/Overlays";
import {
  Interactions,
  DefaultInteractions,
} from "@/components/Map/Interactions";
import {
  selectedStyle
} from "@/components/Map/mapStyles";
import UserContext from "@/components/UserContext/UserContext";
import DialogContext from "@/ui/DialogContext/DialogContext";
import { DCRecord } from "@/@types/lib/dataController";
import { GFIFeatureType } from "@/@types/services/gsService";

import GeoBaseLayerSwitcher from "@/components/Map/Controls/GEO/GeoBaseLayerSwticher";
import GeoAPILayers from "@/components/Map/Layers/GEO/GeoAPILayers";
import RoutingSegmentInfoCard from "./RoutingSegmentInfoCard";

//Services
import mapService from "@/services/mapService";
import gsService from "@/services/gsService";


function RoutingSupportMap(props: IMapProps) {
  const userContext = useContext(UserContext);
  const dialogContext = useContext(DialogContext);
  const { t } = useTranslation();

  const mapId = 9;

  const [mapInitialized, setMapInitialized] = useState(false);

  const [selectedSource, setSelectedSource] = useState<
    OlSourceVector<OlFeature<Geometry>>
  >(new OlSourceVector({}));


  // localStorage map state
  const mapStateJSON = localStorage.getItem(
    'map_state_' + userContext?.firstName.toLowerCase()
  );
  const [viewOptions, setViewOptions] = useState<ViewOptionsType>({
    center:
      mapStateJSON && mapStateJSON !== null &&
        mapStateJSON !== undefined && JSON.parse(mapStateJSON).center &&
        JSON.parse(mapStateJSON).center !== null
        ? JSON.parse(mapStateJSON).center
        : [1731757, 5581737],
    zoom:
      mapStateJSON && mapStateJSON !== null &&
        mapStateJSON !== undefined && JSON.parse(mapStateJSON).zoom &&
        JSON.parse(mapStateJSON).zoom !== null
        ? JSON.parse(mapStateJSON).zoom
        : 8,
    //extent: userContext?.mapSettings ? userContext.mapSettings.max_extent : undefined,
    extent: [1447153, 5104226, 2162937, 5867310],
    constrainOnlyCenter: true,
    //projection: wgs84PM,
    minZoom: 8,
    maxZoom: 18,
    constrainResolution: true,
  });


  const [chosenCoordinateSystem, setChosenCoordinateSystem] =
    useState<CoordinateSystemType>("WGS84");
  const [layersCollection, setLayersCollection] = useState<
    OlCollection<OlBaseLayer> | undefined
  >(undefined);
  const [forceRefreshCounter, setForceRefreshCounter] = useState(0);
  const [feature, setFeature] = useState<OlFeature<Geometry> | undefined>(
    undefined
  );

  const [cardOverlay, setCardOverlay] = useState<ICardOverlay | undefined>(
    undefined
  );
  const [defaultExtent, setDefaultExtent] = useState(
    userContext?.mapSettings?.default_extent
      ? userContext.mapSettings.default_extent
      : [1437016, 5271097, 2206278, 5860579]
  );

  //define proj
  proj4.defs(
    "EPSG:3765",
    "+proj=tmerc +lat_0=0 +lon_0=16.5 +k=0.9999 +x_0=500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs"
  );
  proj4.defs("EPSG:4326", "+proj=longlat +datum=WGS84 +no_defs");
  proj4.defs(
    "EPSG:31276",
    "+proj=tmerc +pm=greenwich +lat_0=0 +lon_0=18 +k=0.9999 +x_0=6500000 +y_0=0 +ellps=bessel +towgs84=550.499,164.116,475.142,5.80967,2.07902,-11.62386,0.99999445824 +units=m +no_defs"
  );
  proj4.defs(
    "EPSG:3857",
    "+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext  +no_defs"
  );
  OlRegister(proj4);

  // defaultViewCenter = [1731757, 5581737];
  // initialDefaultExtent = [1688674,5501166,1688774,5501266];
  const defaultViewCenter = userContext?.mapSettings?.initial_view_center;
  const initialDefaultExtent = userContext?.mapSettings?.default_extent;

  useEffect(() => {
    getLayers();
  }, []);

  const getLayers = () => {
    mapService.getLayers(mapId).then((coll) => {
      setLayersCollection(coll);
      setMapInitialized(true);
    });
  };

  const handleClick = useCallback(
    (evt: OlMapBrowserEvent<any>) => {
     
      gsService
        .getFeatureInfo(evt.map, evt.pixel, ["HGSS:segmenti"])
        .then((resp) => {
          if (resp && Object.keys(resp).length != 0) {
            const layerKeys = Object.keys(resp);
            const segmentiKeys = layerKeys.filter((k) => k.slice(0, 8) === "segmenti");
            const lKey = segmentiKeys.length > 0 ? segmentiKeys[0] : layerKeys[0];
            const features = resp[lKey];
            const firstLayer = resp[lKey];
            const firstFeature = firstLayer[0];
            const featureType = "generic";
            const segmentiLayers: GFIFeatureType[] = segmentiKeys.reduce(
              (acc: GFIFeatureType[], curr: string) => {
                acc.push(...resp[curr]);
                return acc;
              },
              []
            );

            if (segmentiLayers.length > 1) {
              const currentView = evt.map.getView();
              const currentZoom = currentView.getZoom();
              if (
                firstFeature &&
                currentZoom !== undefined &&
                currentZoom < 20
              ) {
                if (firstFeature.geometry.coordinates.length === 2) {
                  currentView.animate({
                    center: firstFeature.geometry.coordinates as [
                      number,
                      number
                    ],
                    zoom: currentZoom + 2,
                  });
                  // if we manage to zoom in dont raise overlay
                  return;
                }
              }
            }

            const fId = isNaN(parseInt(firstFeature.id))
              ? firstFeature.id
              : parseInt(firstFeature.id);
            const data = {
              position: evt.coordinate as CardPosition,
              feature: firstFeature,
              type: featureType as CardOverlayType,
              record: {
                id: fId,
                ...firstFeature.properties
              },
              layerId: lKey,
            } as ICardOverlayData;

            showCardOverlay("gsinfo", data, null);


            const olFeat = new OlFeature();
            switch (firstFeature.geometry.type) {
              case "Point":
                olFeat.setGeometry(
                  new OlPoint(firstFeature.geometry.coordinates as number[])
                );
                break;
              case "LineString":
                olFeat.setGeometry(
                  new OlLineString(
                    firstFeature.geometry.coordinates as number[][]
                  )
                );
                break;
              case "MultiLineString":
                olFeat.setGeometry(
                  new OlMultiLineString(
                    firstFeature.geometry.coordinates as number[][][]
                  )
                );
            }
            setSelectedSource(new OlSourceVector({ features: [olFeat] }));
          } else {
            setSelectedSource(new OlSourceVector({}));
            closeOverlays();
          }
        });

    },
    [layersCollection]
  );

  const showCardOverlay = (
    type: CardOverlayType,
    data: ICardOverlayData,
    ft: OlFeature<any> | null
  ) => {
    //TODO: refactor
    feature ? feature.setProperties({ selected: false }) : null;

    if (ft) {
      ft.setProperties({ selected: true });
    }

    if (type === "gsinfo") {
      setCardOverlay({
        type: type,
        data: data,
      });
      if (ft) {
        setFeature(ft);
      } else {
        setFeature(undefined);
      }
    }
  };

  const closeOverlays = (callback = () => { }) => {
    setCardOverlay(undefined);
    setSelectedSource(new OlSourceVector({}));
  };

  const updateCardOverlay = (record: DCRecord) => {
    setCardOverlay((oldCardOverlay) => {
      if (oldCardOverlay === undefined) return oldCardOverlay;
      const { data, type } = oldCardOverlay;
      return {
        data: { ...data, record: record },
        type,
      };
    });
  };


  const gsOverlay =
    cardOverlay && cardOverlay.type === "gsinfo" ? cardOverlay : undefined;


  return (
    <Fragment>
      {userContext && layersCollection ? (
        <Map
          mapId={mapId}
          height="800px"
          view={viewOptions}
          onClick={handleClick}
          //onMoveend={handleViewChangeCenter}
          className="routing-map"
          id="main-map"
          initialized={mapInitialized}
          moveTolerance={10}
          maxTilesLoading={10}
        >
          <Controls>
            <CoordZoomStatusControl
              chosenCoordinateSystem={chosenCoordinateSystem}
            />
            <ScaleRatioControl ppi={96} />
            <ScaleLineControl />
            <RotateControl
              autoHide={false}
              tipLabel={t("map:controls.reset_north")}
            />
            <GeoBaseLayerSwitcher mapId={mapId} allowNoLayer={false} />
          </Controls>
          <Layers>
            {layersCollection ? (
              <Layers>
                <GeoAPILayers layersCollection={layersCollection} />
                <VectorLayer
                  id="selected"
                  source={selectedSource}
                  style={selectedStyle}
                  zIndex={800}
                />
              </Layers>
            ) : null}
          </Layers>
          <Overlays>
            <PopupOverlay
              id="feature-overlay"
              position={gsOverlay ? gsOverlay.data.position : undefined}
              autoPan={true}
              onClose={() => { }}
            >
              {gsOverlay ? (
                <RoutingSegmentInfoCard
                  featureData={gsOverlay.data}
                  onClose={closeOverlays}
                  updateCardOverlay={updateCardOverlay}
                  refreshLayers={() => { }}
                />
              ) : null}
            </PopupOverlay>
          </Overlays>
          <Interactions>
            <DefaultInteractions />
          </Interactions>
        </Map>
      ) : null}
    </Fragment>
  );
}

export default RoutingSupportMap;
